/**
 * Phase 126 — Organizer Manage Surface at /arenax/host/organizer/manage.
 *
 * Mythology orchestration command center.  Tournaments, leagues, city
 * traction.  Bridges into /arenax/ops/mythology in a future phase.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import OperatorCockpit from '@/components/arenax/OperatorCockpit';
import {
  Trophy, ArrowLeft, Loader2, Calendar, Users, Flame, Video,
  ShieldCheck, ArrowRight, BarChart3, Crown,
} from 'lucide-react';

export default function ArenaxHostOrganizerManage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [organizerId, setOrganizerId] = useState(null);
  const [primaryName, setPrimaryName] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get('/arenax/host/me/primary-entities');
        const oid = r.data?.organizer?.id;
        // Phase 178.5.B — capture canonical name for instant header paint.
        if (r.data?.organizer) {
          setPrimaryName(r.data.organizer.organizer_name
                          || r.data.organizer.organization_name
                          || null);
        }
        if (!oid) { setLoading(false); return; }
        setOrganizerId(oid);
        const ovr = await api.get(`/arenax/host/organizer/${oid}/overview`);
        setOverview(ovr.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not load organizer');
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (!user) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <Trophy className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          <h1 className="text-2xl font-bold mb-2"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            Sign in to manage your tournaments
          </h1>
          <button onClick={() => navigate('/arenax/auth/login')}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold"
                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}>
            Sign in
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  if (loading) {
    return (
      <ArenaxLayout>
        <div className="flex justify-center py-32">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
        </div>
      </ArenaxLayout>
    );
  }

  if (!organizerId) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <Trophy className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          <h1 className="text-2xl font-bold mb-2"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            You're not an organizer yet
          </h1>
          <p className="text-sm mb-6" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            Activate organizer to start running city tournaments.
          </p>
          <button onClick={() => navigate('/arenax/host/organizer')}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold"
                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                  data-testid="organizer-manage-activate-cta">
            Become organizer
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  return (
    <ArenaxLayout>
      <section className="max-w-6xl mx-auto px-5 py-8 arenax-themed space-y-8" data-testid="arenax-organizer-manage-page">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button onClick={() => navigate('/arenax/host')}
                    className="text-xs font-semibold flex items-center gap-1 mb-2"
                    style={{ color: 'hsl(var(--ax-text-muted))' }}
                    data-testid="organizer-manage-back">
              <ArrowLeft className="h-3 w-3" /> Back to hub
            </button>
            <h1 className="text-3xl sm:text-4xl font-light tracking-tight"
                style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, "Times New Roman", serif' }}
                data-testid="organizer-manage-header-name">
              {overview?.organizer_name || primaryName || 'Your league'}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>
              Tournament orchestration · The rivalries that turn into mythology.
            </p>
          </div>
          {overview?.verification_status === 'verified' ? (
            <div className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
                 style={{ background: 'rgba(16,185,129,0.10)', color: '#047857', border: '1px solid rgba(16,185,129,0.30)' }}>
              <ShieldCheck className="h-3 w-3" /> Verified organizer
            </div>
          ) : (
            <div className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
                 style={{ background: 'var(--ax-card-bg-soft)', color: 'hsl(var(--ax-text-muted))', border: '1px solid var(--ax-border-quiet)' }}>
              <ShieldCheck className="h-3 w-3" /> Verification pending
            </div>
          )}
        </div>

        {/* Operator Cockpit — Phase 178.6 (replaces flat tile grid) */}
        <OperatorCockpit kind="organizer" entityId={organizerId} overview={overview} />

        {/* Trust + cadence card */}
        <div className="arenax-card arenax-operational-form p-6 rounded-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold mb-1"
                   style={{ color: 'hsl(var(--ax-text-faint))' }}>Trust score</div>
              <div className="text-2xl font-bold" style={{ color: 'hsl(var(--ax-text))' }}>
                {overview?.trust_score ?? 0}
                <span className="text-xs ml-1 opacity-60">/ 100</span>
              </div>
              <div className="text-[10px] mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                Earned via completed leagues, on-time fixtures, attendance rate.
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold mb-1"
                   style={{ color: 'hsl(var(--ax-text-faint))' }}>Cadence</div>
              <div className="text-base font-bold capitalize" style={{ color: 'hsl(var(--ax-text))' }}>
                {(overview?.recurring_frequency || 'weekly').replace('_', ' ')}
              </div>
              <div className="text-[10px] mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                Set during onboarding · Edit in organizer settings.
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold mb-1"
                   style={{ color: 'hsl(var(--ax-text-faint))' }}>Type</div>
              <div className="text-base font-bold capitalize" style={{ color: 'hsl(var(--ax-text))' }}>
                {(overview?.organizer_type || 'league operator').replace('_', ' ')}
              </div>
              <div className="text-[10px] mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                Drives discovery and squad-fit suggestions.
              </div>
            </div>
          </div>
        </div>

        {/* Modules grid */}
        <div>
          <h2 className="text-base font-bold tracking-tight mb-3" style={{ color: 'hsl(var(--ax-text))' }}>
            Operations
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Module
              icon={Trophy} title="Create tournament"
              subtitle="Spin up a new league or one-off event"
              testId="organizer-manage-module-create-tournament"
              onClick={() => navigate('/arenax/tournaments?new=true')}
            />
            <Module
              icon={Calendar} title="Fixtures"
              subtitle="Match schedule across active leagues"
              testId="organizer-manage-module-fixtures"
              onClick={() => navigate('/arenax/tournaments')}
            />
            <Module
              icon={Users} title="Squad registrations"
              subtitle="Review and approve new entries"
              testId="organizer-manage-module-squads"
              onClick={() => navigate('/arenax/squads')}
            />
            <Module
              icon={Flame} title="Rivalries forming"
              subtitle="Squads playing each other 3+ times"
              testId="organizer-manage-module-rivalries"
              comingSoon
            />
            <Module
              icon={Video} title="Clips generated"
              subtitle="Creator-uploaded highlights from your events"
              testId="organizer-manage-module-clips"
              comingSoon
            />
            <Module
              icon={BarChart3} title="City traction"
              subtitle="Player retention, attendance, completion rate"
              testId="organizer-manage-module-traction"
              comingSoon
            />
          </div>
        </div>
      </section>
    </ArenaxLayout>
  );
}

function Tile({ icon: Icon, label, value, hint }) {
  return (
    <div className="arenax-card p-4 rounded-xl" data-testid={`organizer-tile-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider font-bold"
              style={{ color: 'hsl(var(--ax-text-faint))' }}>{label}</span>
        <Icon className="h-3.5 w-3.5" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
      </div>
      <div className="text-xl font-bold tracking-tight" style={{ color: 'hsl(var(--ax-text))' }}>
        {value}
      </div>
      {hint && <div className="text-[10px] mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>{hint}</div>}
    </div>
  );
}

function Module({ icon: Icon, title, subtitle, onClick, comingSoon, testId }) {
  const content = (
    <>
      <div className="flex items-center justify-between mb-2">
        <Icon className="h-4 w-4" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
        {comingSoon ? (
          <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                style={{ background: 'var(--ax-card-bg-soft)', color: 'hsl(var(--ax-text-faint))' }}>
            Soon
          </span>
        ) : (
          <ArrowRight className="h-3.5 w-3.5" style={{ color: 'hsl(var(--ax-text-faint))' }} />
        )}
      </div>
      <div className="text-sm font-bold mb-0.5" style={{ color: 'hsl(var(--ax-text))' }}>{title}</div>
      <div className="text-[11px]" style={{ color: 'hsl(var(--ax-text-muted))' }}>{subtitle}</div>
    </>
  );
  const className = "arenax-card text-left p-4 rounded-xl transition-all hover:-translate-y-0.5 cursor-pointer";
  if (comingSoon) {
    return <div className={className + ' opacity-60 cursor-default'} data-testid={testId}>{content}</div>;
  }
  return (
    <button type="button" onClick={onClick} className={className + ' w-full block'} data-testid={testId}>
      {content}
    </button>
  );
}
